import { useState, useEffect, createContext, useContext, useCallback, type ReactNode } from 'react';
import { getSupabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AdminAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  staffName: string;
  staffEmail: string;
  staffRole: string;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthState>({
  isAuthenticated: false,
  isLoading: true,
  staffName: '',
  staffEmail: '',
  staffRole: '',
  login: async () => ({ success: false }),
  logout: async () => {},
});

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [staffName, setStaffName] = useState('');
  const [staffEmail, setStaffEmail] = useState('');
  const [staffRole, setStaffRole] = useState('');

  useEffect(() => {
    const supabase = getSupabase();

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        handleSession(session);
      } else {
        setIsLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        handleSession(session);
      } else {
        setIsAuthenticated(false);
        setStaffName('');
        setStaffEmail('');
        setStaffRole('');
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSession = async (session: Session) => {
    const supabase = getSupabase();
    setStaffEmail(session.user.email || '');

    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, role')
      .eq('id', session.user.id)
      .maybeSingle();

    setIsAuthenticated(true);
    setStaffName(profile?.full_name || session.user.email || '');
    setStaffRole(profile?.role || 'staff');
    setIsLoading(false);
  };

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      return { success: false, error: error.message };
    }

    if (data.session) {
      handleSession(data.session);
      return { success: true };
    }

    return { success: false, error: 'Login failed. Please try again.' };
  }, []);

  const logout = useCallback(async () => {
    const supabase = getSupabase();
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setStaffName('');
    setStaffEmail('');
    setStaffRole('');
  }, []);

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, isLoading, staffName, staffEmail, staffRole, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}